import styled from "styled-components";
import { motion } from "framer-motion";
import { PageContainer, getToneTokens } from "../../styles/shared";
import { mq } from "../../styles/canonical/breakpoints";

/* Altura de linha por densidade (Configurações → Tema & Cores):
   Compacto = 25px · Equilibrado (cozy) = 37px · Confortável (comfort) = 50px.
   "default" (páginas fora do fluxo) mantém o comportamento por padding. */
const ALTURA_LINHA = { compact: "25px", cozy: "37px", comfort: "50px" };
const alturaLinha = (d) => ALTURA_LINHA[d] || "auto";
const ALTURA_HEADER = { compact: "30px", cozy: "42px", comfort: "46px" };
const alturaHeader = (d) => ALTURA_HEADER[d] || "auto";
const ehCompacto = (d) => d === "compact";

/* $fluxo: só as 15 telas do Gerenciador de Produção recebem o re-skin do
   mockup (2026-07). Fora do fluxo (Clientes, Orçamentos, VisãoFinanceira…
   que também usam PainelTabela) tudo fica com o visual original. */
export const Wrapper = styled(PageContainer)`
  gap: 12px;

  /* Celular e tablet (redesenho mobile do legado, 2026-09-24): tudo mais
     perto. O breakpoint é o canônico (1024) — antes eram 720/860/920 soltos. */
  @media ${mq.tablet} {
    gap: 8px;
  }

  /* No fluxo: largura do mockup (1560) centrada. O brilho radial fica no
     MAIN (App.jsx), não aqui, pra cobrir a página toda. */
  ${({ $fluxo }) => ($fluxo ? "max-width: 1560px; margin: 0 auto;" : "")}
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;

  @media ${mq.tablet} {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }
`;

export const Titulo = styled.h1`
  font-size: clamp(1.5rem, 2vw, 1.9rem);
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
  font-family: var(--font-display);
  flex: 1 0 auto;

  @media ${mq.tablet} {
    font-size: 1.15rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;

  /* Celular/tablet: uma linha só — busca cresce, os botões viram ícone. */
  @media ${mq.tablet} {
    width: 100%;
    gap: 6px;
  }
`;

/* Escondido no celular/tablet (restaurar larguras de coluna não faz sentido
   em cards) e botão "Exportar painel" só com o ícone — peças compartilhadas. */
export { SoDesktop, SoIcone as ExportCompacto } from "../ListaCompacta/styles";

export const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
  gap: 12px;
  padding: 0 16px 16px;
`;

export const FilterField = styled.label`
  display: grid;
  gap: 6px;
`;

export const FilterLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.textSecondary};
`;

export const FilterInlineGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const controlCss = `
  width: 100%;
  min-height: 40px;
  padding: 9px 12px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  outline: none;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:focus {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surface};
  }
`;

export const BuscaInput = styled.input`
  ${controlCss}
  width: clamp(190px, 22vw, 280px);

  @media ${mq.tablet} {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    min-height: 36px;
  }
`;

export const FiltroSelect = styled.select`
  ${controlCss}
  cursor: pointer;
`;

/* Busca no visual do mockup (fluxo): ícone à esquerda + retângulo
   arredondado, borda em foco no accent. */
export const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  min-width: 236px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textTertiary};
  transition: border-color 0.16s ease;

  &:focus-within {
    border-color: ${({ theme }) => theme.accent};
  }

  svg {
    flex: none;
  }

  input {
    width: 100%;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    font-size: 13px;
  }

  @media ${mq.tablet} {
    flex: 1 1 auto;
    min-width: 0;
    height: 36px;
    padding: 0 10px;
  }
`;

export const Totalizadores = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 12px;

  /* Celular/tablet: faixa rolável de tiles pequenos, em vez de 3+ cards
     empilhados que empurravam o primeiro item para 520px do topo. */
  @media ${mq.tablet} {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    scrollbar-width: none;
    margin: 0 -16px;
    padding: 0 16px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

/* KPI card. No fluxo ($fluxo): efeito glass do mockup (gradiente + blur +
   brilho) em coluna — topo (label + valor na mesma linha de base) e, abaixo,
   barra bicolor + legenda (card Total) ou subtítulo. Fora do fluxo: card
   original (label acima do valor). Tom de estado (danger) preservado. */
export const TotalCard = styled.div`
  ${({ theme, $tone = "default", $fluxo }) => {
    const tokens = getToneTokens(theme, $tone);
    const glass = $fluxo && $tone === "default";

    return `
      background: ${
        glass
          ? `linear-gradient(155deg, ${theme.surfaceAlt}, ${theme.surface})`
          : tokens.background
      };
      border: 1px solid ${tokens.border};
    `;
  }}

  min-width: 0;
  border-radius: ${({ $fluxo }) => ($fluxo ? "13px" : "12px")};
  padding: ${({ $fluxo }) => ($fluxo ? "12px 15px" : "14px 16px")};

  ${({ $fluxo }) =>
    $fluxo
      ? `
    display: flex;
    flex-direction: column;
    gap: 9px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(13px) saturate(1.3);
    -webkit-backdrop-filter: blur(13px) saturate(1.3);
  `
      : ``}

  /* Export (html2canvas ignora blur): sem o blur, o gradiente glass vira
     duas faixas de cor visiveis no JPG/PNG — achata para superficie solida
     apenas durante a captura. */
  body[data-export-mode="true"] & {
    background: ${({ theme, $tone = "default", $fluxo }) =>
      $fluxo && $tone === "default"
        ? theme.surfaceAlt
        : getToneTokens(theme, $tone).background};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  @media ${mq.tablet} {
    flex: 0 0 auto;
    min-width: 92px;
    padding: 7px 10px;
    border-radius: 10px;
    gap: 0;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
`;

/* Topo do card: no fluxo, label e valor na mesma linha de base; fora do
   fluxo, "display: contents" preserva o empilhamento original. */
export const TotalTopo = styled.div`
  ${({ $fluxo }) =>
    $fluxo
      ? `display: flex; align-items: baseline; justify-content: space-between; gap: 8px;`
      : `display: contents;`}

  /* Tile compacto: número em cima, rótulo embaixo. */
  @media ${mq.tablet} {
    display: flex;
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 3px;
  }
`;

export const TotalLabel = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  font-size: ${({ $fluxo }) => ($fluxo ? "10px" : "11px")};
  ${({ $fluxo }) => ($fluxo ? `` : `margin-bottom: 6px;`)}
  color: ${({ theme, $tone = "default", $fluxo }) => {
    if ($tone !== "default") return getToneTokens(theme, $tone).text;
    return $fluxo ? theme.textTertiary : theme.textSecondary;
  }};

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
    letter-spacing: 0.04em;
    margin: 0;
    white-space: nowrap;
  }
`;

export const TotalValor = styled.div`
  color: ${({ theme, $tone = "default" }) => getToneTokens(theme, $tone).text};
  overflow-wrap: anywhere;
  word-break: break-word;
  ${({ $fluxo }) =>
    $fluxo
      ? `font-family: var(--font-display); font-size: 23px; font-weight: 600; line-height: 1;`
      : `font-size: clamp(1.45rem, 2.6vw, 1rem); font-weight: 700; line-height: 1.1;`}

  @media ${mq.tablet} {
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

/* Barra bicolor no-prazo/vencido do card "Total de ambientes" (mockup). */
export const TotalBar = styled.div`
  display: flex;

  @media ${mq.tablet} {
    display: none;
  }
  height: 5px;
  border-radius: 5px;
  overflow: hidden;
  background: ${({ theme }) => theme.surfaceAlt};
`;

/* Preenchimentos animados via framer-motion (entra 0→%, sai %→0) — a
   animação de entrada/saída é controlada pelo componente. */
export const TotalBarOk = styled(motion.div)`
  background: var(--success-text);
`;

export const TotalBarVenc = styled(motion.div)`
  background: var(--danger-text);
`;

export const TotalLegend = styled.div`
  display: flex;
  gap: 12px;

  @media ${mq.tablet} {
    display: none;
  }
  font-size: 10.5px;
  color: ${({ theme }) => theme.textTertiary};

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  span::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  .ok::before {
    background: var(--success-text);
  }
  .venc::before {
    background: var(--danger-text);
  }
`;

/* Sub-linha do KPI (ex.: "de 6 ambientes"). */
export const TotalSub = styled.div`
  font-size: 10.5px;
  color: ${({ theme }) => theme.textTertiary};

  @media ${mq.tablet} {
    display: none;
  }
`;

export const TabelaWrapper = styled.div`
  /* Sem scroll interno — o scroll é da pagina inteira.
     O sticky do <Th> referencia o body como ancestor scrollavel e usa
     top: var(--app-header-height) para ficar abaixo do Header global. */
  width: 100%;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
`;

export const Tabela = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${({ $density }) => ($density === "compact" ? "11px" : "13px")};
  /* Bloco TBL.3 (2026-05-25): table-layout fixed em ambas densidades.
     Permite text-overflow: ellipsis funcionar no Td (precisa de width
     respeitada). compact mantem min-width:0 (evita scroll-x); default
     mantem 760px para nao virar uma fileira esticada quando o container
     ainda eh grande. */
  table-layout: fixed;
  ${({ $density }) =>
    $density === "compact" ? `min-width: 0;` : `min-width: 760px;`}
`;

export const Th = styled.th`
  height: ${({ $density }) => alturaHeader($density)};
  padding: ${({ $density }) => (ehCompacto($density) ? "2px 12px" : "0 16px")};
  text-align: left;
  /* Bloco TBL.5 (2026-05-29 — #13 fix v2 apos debug visual): sticky
     header com background OPACO. Versao anterior usava color-mix com
     alpha 0.88 + backdrop blur — mas o alpha deixava linhas da tabela
     ficarem parcialmente visiveis ATRAS do cabecalho durante scroll,
     criando efeito de "duas linhas empilhadas". Background opaco
     (theme.surfaceAlt sem alpha) elimina o problema. */
  position: sticky;
  top: var(--app-header-height, 0px);
  z-index: 5;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  font-weight: 600;
  font-size: ${({ $density }) => ($density === "compact" ? "10px" : "11px")};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;
  white-space: nowrap;
  /* A régua do cabeçalho é o border; a sombra inset só a reforça na tela.
     Em modo de exportação a sombra é zerada (GlobalStyles) e o border sozinho
     mantém a régua na imagem — por isso não há nada a redesenhar aqui. */
  border-bottom: 1px solid ${({ theme }) => theme.border};
  box-shadow: inset 0 -1px 0 ${({ theme }) => theme.border};
  user-select: none;

  &:hover {
    color: ${({ theme }) => theme.accent};
  }
`;

export const ThInner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const ThIconHint = styled.span`
  opacity: 0.3;
  display: inline-flex;
`;

/* Funil de filtro por coluna (visual do mockup 2026-07). Abre o painel de
   Filtros existente; só aparece no fluxo. */
export const ThFunil = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  margin-left: 2px;
  border: none;
  background: transparent;
  color: ${({ theme, $ativo }) => ($ativo ? theme.accent : theme.textTertiary)};
  opacity: ${({ $ativo }) => ($ativo ? 1 : 0.55)};
  cursor: pointer;
  border-radius: 4px;
  transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
`;

/* Atalho "Só vencidos" (fluxo) — no lugar do botão de Filtros do mockup. */
export const SoVencidosBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 13px;
  border-radius: 10px;
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $ativo }) => ($ativo ? 600 : 500)};
  cursor: pointer;
  transition: border-color 0.16s ease, color 0.16s ease, background 0.16s ease;
  border: 1px solid
    ${({ theme, $ativo }) => ($ativo ? theme.dangerBorder : theme.border)};
  background: ${({ theme, $ativo }) =>
    $ativo ? theme.dangerSurface : theme.surface};
  color: ${({ theme, $ativo }) =>
    $ativo ? theme.dangerText : theme.textSecondary};

  &:hover {
    border-color: ${({ theme, $ativo }) =>
      $ativo ? theme.dangerBorder : theme.border2 || theme.border};
    color: ${({ theme, $ativo }) => ($ativo ? theme.dangerText : theme.textPrimary)};
  }

  /* Celular/tablet: só o ícone (36px), rótulo some. */
  @media ${mq.tablet} {
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    padding: 0;
    gap: 0;
    justify-content: center;
    border-radius: 9px;

    .rotulo {
      display: none;
    }
  }
`;

/* Popover de filtro por coluna (estilo Excel) — fixo, ancorado ao funil. */
export const FunilPopover = styled.div`
  position: fixed;
  z-index: 200;
  width: 250px;
  max-height: 340px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.45);
  overflow: hidden;
`;

export const FunilHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border};

  span {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textTertiary};
  }
`;

export const FunilLimpar = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.accent};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const FunilBusca = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 10px 12px 6px;
  padding: 0 10px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.textTertiary};

  &:focus-within {
    border-color: ${({ theme }) => theme.accent};
  }

  input {
    width: 100%;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    font-size: 13px;
  }
`;

export const FunilLista = styled.div`
  overflow-y: auto;
  padding: 4px 6px 8px;
  scrollbar-width: thin;
`;

export const FunilItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 13px;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  input {
    accent-color: ${({ theme }) => theme.accent};
    flex: none;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const FunilVazio = styled.div`
  padding: 12px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.textTertiary};
  text-align: center;
`;

/* Linha da tabela (motion.tr). O tint de status vive AQUI (não em style
   inline) para o :hover conseguir sobrepor — como no mockup, o hover
   SUBSTITUI o tint pelo destaque neutro (transition 0.12s). Hover só no
   fluxo; fora dele a linha mantém o comportamento original. */
export const TrLinha = styled(motion.tr)`
  /* Tint SUAVE via token pré-calculado (--{tone}-row = metade da intensidade
     do *-bg). NÃO usar color-mix aqui: o Chrome computa color-mix como
     color(srgb ...) e o html2canvas 1.4 (export JPG/PNG) não parseia essa
     função — a exportação inteira quebrava. */
  ${({ $tint }) => ($tint ? `background: var(--${$tint}-row);` : "")}
  ${({ $clicavel }) => ($clicavel ? "cursor: pointer;" : "")}
  ${({ $fluxo }) =>
    $fluxo
      ? `
    transition: background 0.12s ease;
    &:hover {
      background: var(--surface-active);
    }
  `
      : ""}
`;

export const Td = styled.td`
  /* Altura explícita por densidade (25/37/50); padding só horizontal — o
     conteúdo centraliza vertical (vertical-align: middle). */
  height: ${({ $density }) => alturaLinha($density)};
  padding: ${({ $density }) => (ehCompacto($density) ? "2px 10px" : "0 16px")};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textPrimary};
  font-size: ${({ $density }) => ($density === "compact" ? "11px" : "13px")};
  line-height: ${({ $density }) => ($density === "compact" ? "1.25" : "1.35")};
  /* Bloco TBL.3 (2026-05-25): truncate + ellipsis por default. Quando o
     conteudo nao cabe, mostra reticencias e o atributo title da cell
     (setado pelo PainelTabela via tituloCelula) exibe o texto completo
     no hover. compact mantem wrap (denso, texto curto cabe; quebrar eh
     OK). Status (badge) tambem mantem normal (texto curto, sem ellipsis). */
  white-space: ${({ $density, $isStatus }) =>
    $isStatus || $density === "compact" ? "normal" : "nowrap"};
  overflow: ${({ $density, $isStatus }) =>
    $isStatus || $density === "compact" ? "visible" : "hidden"};
  text-overflow: ${({ $density, $isStatus }) =>
    $isStatus || $density === "compact" ? "clip" : "ellipsis"};
  overflow-wrap: ${({ $density }) =>
    $density === "compact" ? "anywhere" : "normal"};
  word-break: ${({ $density }) =>
    $density === "compact" ? "break-word" : "normal"};
  max-width: ${({ $isStatus }) => ($isStatus ? "220px" : "none")};
  vertical-align: middle;

  /* Barra de status à esquerda da linha (assinatura do mockup) — aplicada
     só na 1ª célula, na cor do estado, só no fluxo.

     Desenhada como pseudo-elemento, e NÃO como box-shadow inset. O
     html2canvas até suporta sombra inset, mas para pintá-la ele recorta a
     border-box e usa como máscara o retângulo do canvas MENOS a célula —
     geometria que na captura acaba preenchendo a célula INTEIRA com a cor da
     barra. Era o bloco rosa/verde cobrindo o nome do cliente nas imagens
     exportadas (Liberação PPCP, Compras). Background de pseudo-elemento passa
     pelo caminho normal de clonagem e sai igual à tela. */
  ${({ $barra }) =>
    $barra && $barra !== "default"
      ? `
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: var(--${$barra}-text, currentColor);
      pointer-events: none;
    }
  `
      : ``}

  /* Coluna marcada com noWrap (ex: "Cliente"): nunca quebra; trunca com
     ellipsis se nao couber. Override do comportamento compact. */
  &[data-no-wrap="true"] {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: normal;
    overflow-wrap: normal;
  }

  tr:last-child & {
    border-bottom: none;
  }
`;

export const StatusBadge = styled.span`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);

    return `
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}

  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-height: ${({ $density }) => ($density === "compact" ? "16px" : "28px")};
  padding: ${({ $density }) => ($density === "compact" ? "1px 8px" : "5px 10px")};
  border-radius: 999px;
  font-size: ${({ $density }) => ($density === "compact" ? "10px" : "12px")};
  font-weight: 600;
  line-height: ${({ $density }) => ($density === "compact" ? "1.1" : "1.3")};
  /* No fluxo o badge NUNCA quebra ("Venc/ido" em 2 linhas) — mockup. */
  white-space: ${({ $fluxo }) => ($fluxo ? "nowrap" : "normal")};
  overflow-wrap: ${({ $fluxo }) => ($fluxo ? "normal" : "anywhere")};
  word-break: ${({ $fluxo }) => ($fluxo ? "normal" : "break-word")};

  /* Pontinho na cor do estado — assinatura do mockup (2026-07). Só no fluxo
     e quando há estado (tom != default). */
  &::before {
    content: "";
    flex: none;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    display: ${({ $tone = "default", $fluxo }) =>
      $fluxo && $tone !== "default" ? "block" : "none"};
  }
`;

export const EmptyTd = styled.td`
  padding: 40px 24px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
`;

export const Paginacao = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

export const PagInfo = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const PagBotoes = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

export const PagBtn = styled.button`
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 13px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.accent : theme.border)};
  background: ${({ theme, $active }) => ($active ? theme.accent : theme.surface)};
  color: ${({ theme, $active }) => ($active ? "#ffffff" : theme.textSecondary)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.accent};
    color: ${({ theme, $active }) => ($active ? "#ffffff" : theme.accent)};
  }
`;

export const PagEllipsis = styled.span`
  padding: 5px 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
`;

// ============================================================
// Lista em cards (celular e tablet) — peças compartilhadas em
// components/ListaCompacta (o mesmo card serve as telas com layout próprio).
// ============================================================
export {
  CardList,
  CardItem,
  CardTopo,
  CardTitulo,
  CardSub,
  CardMeta,
  CardVazio,
} from "../ListaCompacta/styles";
